var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useTableRowExpansion } from '../../annual/common/selectors';
import { TableBody } from '../../common';
import { QueueType } from '../../multiskill/types';
import { useScenarioPlanIdByColorIndex } from '../../seatCapacity';
import { PLAN_COLORS } from '../../wizard/constants';
import { getCurrencyDecimalValue } from '../constants';
import { PeriodTableHead } from '../periodTable';
import { MainRow } from './mainRow';
import { QUEUE_EXPANSION_KEY_SEPARATOR, QueueRow } from './queueRow';
import { INDENT_LEVELS } from './utils';
var PERIOD_IMPLICIT_CHILD_SEGMENTS = ['lob', 'queue', 'country'];
export var getImplicitNestedPeriodExpansionItems = function (itemName, expansionItems) {
  return expansionItems.filter(function (item) {
    return PERIOD_IMPLICIT_CHILD_SEGMENTS.some(function (segment) {
      return item.startsWith("".concat(itemName).concat(QUEUE_EXPANSION_KEY_SEPARATOR).concat(segment).concat(QUEUE_EXPANSION_KEY_SEPARATOR));
    });
  });
};
export var PeriodSectionTable = function (_a) {
  var _b;
  var title = _a.title,
    data = _a.data,
    index = _a.index,
    addColumns = _a.addColumns,
    isProdHoursPlan = _a.isProdHoursPlan,
    expansionItems = _a.expansionItems,
    TableBodyComponent = _a.tableBodyComponent,
    _c = _a.isExpandable,
    isExpandable = _c === void 0 ? true : _c,
    _d = _a.defaultExpanded,
    defaultExpanded = _d === void 0 ? false : _d,
    _e = _a.defaultExpandedItems,
    defaultExpandedItems = _e === void 0 ? [] : _e,
    _f = _a.firstHeader,
    firstHeader = _f === void 0 ? false : _f,
    scenarioSelector = _a.scenarioSelector,
    summaryColumnLabel = _a.summaryColumnLabel,
    getNestedExpansionItems = _a.getNestedExpansionItems;
  var expansion = useTableRowExpansion(expansionItems, defaultExpanded, defaultExpandedItems);
  var handleToggleItem = function (itemName) {
    var _a;
    var expanded = !expansion.expandedItems[itemName];
    var nestedItems = __spreadArray(__spreadArray([], (_a = getNestedExpansionItems === null || getNestedExpansionItems === void 0 ? void 0 : getNestedExpansionItems(itemName)) !== null && _a !== void 0 ? _a : [], true), getImplicitNestedPeriodExpansionItems(itemName, expansionItems), true);
    expansion.setItemsExpanded([itemName], expanded);
    expansion.setItemsExpanded(Array.from(new Set(nestedItems)), false);
  };
  var getScenarioPlanId = useScenarioPlanIdByColorIndex();
  return _jsxs(_Fragment, {
    children: [_jsx(PeriodTableHead, {
      title: index === 0 ? title : undefined,
      index: index,
      namePlan: data.displayName,
      headColor: (_b = PLAN_COLORS[data === null || data === void 0 ? void 0 : data.colorIndex]) === null || _b === void 0 ? void 0 : _b[0],
      dataType: data.dataType,
      addColumns: addColumns,
      firstHeader: firstHeader,
      // Expansion controls forwarded to head
      isExpandable: isExpandable,
      expanded: expansion.someExpanded,
      onExpandToggle: expansion.toggleAll,
      scenarioSelector: scenarioSelector,
      summaryColumnLabel: summaryColumnLabel,
      planId: getScenarioPlanId(data === null || data === void 0 ? void 0 : data.colorIndex)
    }), _jsx(TableBodyComponent, {
      data: data,
      addColumns: addColumns,
      isProdHoursPlan: isProdHoursPlan,
      expandedItems: expansion.expandedItems,
      onToggleItem: handleToggleItem
    })]
  });
};
export var PeriodSectionWithTotalTable = function (_a) {
  var _b, _c, _d;
  var title = _a.title,
    data = _a.data,
    valueKey = _a.valueKey,
    addColumns = _a.addColumns,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    _e = _a.countryOnly,
    countryOnly = _e === void 0 ? false : _e,
    _f = _a.totalAsParent,
    totalAsParent = _f === void 0 ? false : _f,
    _g = _a.blankTotal,
    blankTotal = _g === void 0 ? false : _g;
  var totalRowValue = data[valueKey];
  var totalRowData = Array.isArray(totalRowValue) ? totalRowValue : undefined;
  var shouldUseCountryHierarchy = countryOnly && !!((_b = data.countries) === null || _b === void 0 ? void 0 : _b.length);
  var precision = getCurrencyDecimalValue(valueKey);
  var isTotalExpanded = (_c = expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[valueKey]) !== null && _c !== void 0 ? _c : false;
  var isMultiskill = data.dataType === QueueType.Multiskill;
  var rowColor = (_d = PLAN_COLORS[data === null || data === void 0 ? void 0 : data.colorIndex]) === null || _d === void 0 ? void 0 : _d[1];
  var totalRow = totalRowData && title && _jsx(MainRow, {
    bold: true,
    data: totalRowData,
    rowColor: rowColor,
    title: title,
    isHideRow: totalAsParent,
    showDetails: isTotalExpanded,
    onShowRow: function () {
      return onToggleItem === null || onToggleItem === void 0 ? void 0 : onToggleItem(valueKey);
    },
    isIncludeTotal: addColumns === null || addColumns === void 0 ? void 0 : addColumns.includes('Total'),
    blankTotal: blankTotal,
    averagePrecision: precision,
    isCurrency: true,
    isMultiskill: isMultiskill
  });
  var queueRows = _jsx(QueueRow, {
    data: data.queues,
    dataWithLob: data.queuesByLob,
    countries: shouldUseCountryHierarchy ? data.countries : undefined,
    isIncludeTotal: addColumns === null || addColumns === void 0 ? void 0 : addColumns.includes('Total'),
    blankTotal: blankTotal,
    keyItem: valueKey,
    rowColor: rowColor,
    averagePrecision: precision,
    isCurrency: true,
    isMultiskill: isMultiskill,
    expandedItems: expandedItems,
    onToggleItem: onToggleItem,
    countryOnly: shouldUseCountryHierarchy,
    queueIndent: totalAsParent ? INDENT_LEVELS.queue : undefined
  });
  var rows = totalAsParent ? _jsxs(_Fragment, {
    children: [totalRow, isTotalExpanded && queueRows]
  }) : _jsxs(_Fragment, {
    children: [queueRows, totalRow]
  });
  return _jsx(TableBody, {
    children: rows
  });
};